import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import 'styles/components/TripsFilter';
import { Badge, Box, Icon, Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import NewSeat from 'ui/atoms/NewSeat';
import adultIcon from '../images/brands/gfa/avatars/adult.png';
import minorIcon from '../images/brands/gfa/avatars/minor.png';
import studentIcon from '../images/brands/gfa/avatars/student.png';
import teacherIcon from '../images/brands/gfa/avatars/teacher.png';
import insenIcon from '../images/brands/gfa/avatars/old-adult.png';
import adultIconVariant from '../images/brands/etn/avatars/adult.png';
import minorIconVariant from '../images/brands/etn/avatars/minor.png';
import studentIconVariant from '../images/brands/etn/avatars/student.png';
import teacherIconVariant from '../images/brands/etn/avatars/teacher.png';
import insenIconVariant from '../images/brands/etn/avatars/old-adult.png';
import adultIconBrand from '../images/brands/empresaunida/icons/ic-adult.png';
import olderIconBrand from '../images/brands/empresaunida/icons/ic-older.png';
import disadvantagedYouthIconBrand from '../images/brands/empresaunida/icons/ic-disadvantaged-youth.png';
import disabilityIconBrand from '../images/brands/empresaunida/icons/ic-disability.png';
import childInArmsIconBrand from '../images/brands/empresaunida/icons/ic-child-in-arms.png';
import childIcon from '../images/passengers/child-icon.png';
import adjacentSeatIcon from '../images/icons/adjacent-seat.webp';
import handicapSeatIcon from '../images/badges/wheelchair.svg';

const types = {
  minor: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    label: 'category_minor',
  },
  teen: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    label: 'category_teen',
  },
  older: {
    icon: insenIcon,
    iconVariant: insenIconVariant,
    iconBrand: olderIconBrand,
    label: 'category_older',
  },
  general: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    iconBrand: adultIconBrand,
    label: 'category_general',
  },
  student: {
    icon: studentIcon,
    iconVariant: studentIconVariant,
    label: 'category_student',
  },
  teacher: {
    icon: teacherIcon,
    iconVariant: teacherIconVariant,
    label: 'category_teacher',
  },
  wheelchair_handicap: {
    icon: null,
    iconVariant: null,
    label: 'category_wheelchair_handicap',
  },
  special: {
    icon: null,
    iconVariant: null,
    label: 'category_special',
  },
  pet_friendly: {
    icon: null,
    iconVariant: null,
    label: 'category_pet_friendly',
  },
  oversold: {
    icon: null,
    iconVariant: null,
    label: 'category_oversold',
  },
  older_half: {
    icon: insenIcon,
    iconVariant: insenIconVariant,
    iconBrand: olderIconBrand,
    label: 'category_older_half',
  },
  free_pass: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    iconBrand: disabilityIconBrand,
    label: 'category_free_pass',
  },
  intermunicipal_free_pass: {
    icon: insenIcon,
    iconVariant: insenIconVariant,
    label: 'category_intermunicipal_free_pass',
  },
  disadvantaged_youth: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    iconBrand: disadvantagedYouthIconBrand,
    label: 'category_disadvantaged_youth',
  },
  disadvantaged_youth_half: {
    icon: minorIcon,
    iconVariant: minorIconVariant,
    iconBrand: disadvantagedYouthIconBrand,
    label: 'category_disadvantaged_youth_half',
  },
  disability: {
    icon: null,
    iconVariant: null,
    iconBrand: disabilityIconBrand,
    label: 'category_disability',
  },
  disability_half: {
    icon: null,
    iconVariant: null,
    iconBrand: disabilityIconBrand,
    label: 'category_disability_half',
  },
  federal_companion_free_pass: {
    icon: adultIcon,
    iconVariant: adultIconVariant,
    label: 'category_federal_companion_free_pass',
  },
  child_in_arms: {
    icon: childIcon,
    iconVariant: childIcon,
    iconBrand: childInArmsIconBrand,
    label: 'category_child_in_arms',
  },
};

/**
 * Renders the PassengerInfoHeader component, which displays passenger information,
 * including the passenger's name, type, category icon, and seat selection details.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.name] - The name of the passenger. If not provided, it defaults to "Passenger X" where X is the passenger number.
 * @param {string} [props.typePassenger='general'] - The type of passenger, which determines the displayed icon and category.
 *    It can be one of the following values:
 *    - "minor" "older" "general" "student""teacher" "wheelchair_handicap" "special""pet_friendly"
 * @param {number} [props.numberSeat=11] - The assigned seat number for the passenger. If not provided, it defaults to 11.
 * @param {number} [props.numberPassenger=1] - The passenger number, used to display "Passenger X" when `name` is not provided.
 *    This is useful when displaying multiple passengers in a list.
 * @param {number} [props.returnSeat=null] - If the passenger has a round trip, this prop will display the seat number for the return trip.
 *    If `null`, no return seat will be displayed.
 * @param {boolean} [props.isAdjacentSeat] - When true, shows adjacent-seat layout instead of standard seat chips
 * @param {number} [props.departureSeatZone] - Elements NewSeat zone (1–5) for departure when pricing-by-zone is on
 * @param {number} [props.returnSeatZone] - Elements NewSeat zone (1–5) for return when pricing-by-zone is on
 * @param {boolean} [props.isRoundTrip=false] - When true, renders the return-leg seat next to the departure seat.
 * @param {boolean} [props.hideSeat=false] - When true, skips the seat section (used by lap-child rows)
 * @param {boolean} [props.isDepartureHandicapSeat=false] - When true, shows an accessible-seat icon before the departure seat chip
 * @param {boolean} [props.isReturnHandicapSeat=false] - When true, shows an accessible-seat icon before the return seat chip
 *
 * @returns {JSX.Element} The rendered PassengerInfoHeader component, which shows the passenger name, type, icon, and seat(s) information.
 */
const PassengerInfoHeader = ({
  name,
  typePassenger,
  returnSeat,
  numberSeat,
  numberPassenger,
  isAdjacentSeat,
  departureSeatZone,
  returnSeatZone,
  isRoundTrip,
  hideSeat,
  isDepartureHandicapSeat,
  isReturnHandicapSeat,
}) => {
  const { t } = useTranslation('general');
  const { t: tPurchase } = useTranslation('purchase');
  const typeConfig = types[typePassenger] || types.general;
  const { icon, iconVariant, iconBrand, label } = typeConfig;

  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const useBrandIcons = features.FREE_FARE_BRAND_GLYPHS;
  const isFlat = features.FUNNEL_STYLE === 'FLAT';
  const categoryIcon = (useBrandIcons && iconBrand) || (isFlat ? iconVariant : icon);

  return (
    <Spacing alignItems="center" isResponsive mobileAlign="flex-start" responsiveSize="S" size="XL">
      <Text mobileSize="S" weight="semibold">
        {name || `${t('passengers:label.passenger')} ${numberPassenger}`}
      </Text>

      <Spacing responsiveSize="M" size="L" alignItems="center">
        {isAdjacentSeat ? (
          <Spacing size="XS" alignItems="center">
            <Icon type={adjacentSeatIcon} />

            <Text mobileSize="S" color="grayMedium">
              {tPurchase('category_adjacent_seat')}
            </Text>
            <NewSeat isSmall number={numberSeat} type="adjacent" useNewDesign />
          </Spacing>
        ) : (
          <Spacing size="XS" alignItems="center">
            {categoryIcon ? (
              <Box
                bgColor="primary"
                alphaBg="XS"
                borderRadius="L"
                paddingHorizontal="XS"
                paddingVertical="XS"
              >
                <Icon type={categoryIcon} size={useBrandIcons ? 'L' : 'S'} />
              </Box>
            ) : null}

            <Text mobileSize="S" color="grayMedium">
              {t(label, { context: env.brand })}
            </Text>
          </Spacing>
        )}

        {isAdjacentSeat || hideSeat ? null : (
          <Spacing size="XS" alignItems="center">
            <Text mobileSize="S" color="grayMedium">
              {t('seat')}
            </Text>
            {isDepartureHandicapSeat && (
              <span
                role="img"
                aria-label={t('seats:seat_handicap')}
                title={t('seats:seat_handicap')}
                className="inline-flex items-center"
              >
                <Icon type={handicapSeatIcon} size="S" />
              </span>
            )}
            {numberSeat ? (
              <NewSeat
                isSmall
                number={numberSeat}
                type="selected"
                zone={departureSeatZone}
                useNewDesign
              />
            ) : (
              <Badge roundCorners type="gray">
                <Text elementType="span" size="S" weight="semibold">
                  {t('text.open_ticket_short')}
                </Text>
              </Badge>
            )}
            {isRoundTrip && isReturnHandicapSeat && (
              <Spacing size="XS" alignItems="center">
                <Text mobileSize="S" color="grayMedium">
                  •
                </Text>
                <span
                  role="img"
                  aria-label={t('seats:seat_handicap')}
                  title={t('seats:seat_handicap')}
                  className="inline-flex items-center"
                >
                  <Icon type={handicapSeatIcon} size="S" />
                </span>
              </Spacing>
            )}
            {isRoundTrip &&
              (returnSeat ? (
                <NewSeat
                  isSmall
                  number={returnSeat}
                  type="selected"
                  zone={returnSeatZone}
                  useNewDesign
                />
              ) : (
                <Badge roundCorners type="gray">
                  <Text elementType="span" size="S" weight="semibold">
                    {t('text.open_ticket_short')}
                  </Text>
                </Badge>
              ))}
          </Spacing>
        )}
      </Spacing>
    </Spacing>
  );
};

PassengerInfoHeader.propTypes = {
  name: PropTypes.string,
  /** Ticket / passenger category from API (e.g. older_half, free_pass). */
  typePassenger: PropTypes.string,
  numberSeat: PropTypes.number,
  numberPassenger: PropTypes.number,
  returnSeat: PropTypes.number,
  isAdjacentSeat: PropTypes.bool,
  departureSeatZone: PropTypes.number,
  returnSeatZone: PropTypes.number,
  isRoundTrip: PropTypes.bool,
  hideSeat: PropTypes.bool,
  isDepartureHandicapSeat: PropTypes.bool,
  isReturnHandicapSeat: PropTypes.bool,
};

PassengerInfoHeader.defaultProps = {
  name: '',
  typePassenger: 'general',
  numberSeat: null,
  numberPassenger: 1,
  returnSeat: null,
  isAdjacentSeat: false,
  departureSeatZone: undefined,
  returnSeatZone: undefined,
  hideSeat: false,
  isRoundTrip: false,
  isDepartureHandicapSeat: false,
  isReturnHandicapSeat: false,
};

export default PassengerInfoHeader;
